import React, { useMemo, useState } from "react";
import { FiHeart, FiStar } from "react-icons/fi";
import { useHistory, useLocation } from "react-router-dom";

import {
  HeaderContainer,
  HeaderInner,
  Brand,
  BrandLogo,
  BrandText,
  BrandTitle,
  BrandSub,
  DesktopNav,
  NavItem,
  HeaderActions,
  HeaderCta,
  MobileToggle,
  MobileMenuOverlay,
  HeaderMobileMenu,
  HeaderMobileItem,
  MobileTop,
  HeaderSwitch,
  HeaderSwitchButton,
  HeaderSwitchIcon,
  HeaderSwitchLabel,
} from "./styled";
import { scrollToSection } from "utils";
import { getLandpageVariantTheme } from "components/Landpage/theme-variants";

export default function Header({ variant = "nursing" }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const pageTheme = getLandpageVariantTheme(variant);
  const isAesthetics = variant === "aesthetics";
  const useAestheticHeader = isAesthetics || variant === "nursing";
  const nursingHomePath = "/enfermagem";
  const aestheticsHomePath = "/estetica";
  const landpages = [
    { key: "nursing", title: "Enfermagem", path: nursingHomePath, icon: FiHeart },
    { key: "aesthetics", title: "Estética", path: aestheticsHomePath, icon: FiStar },
  ];
  const isNursingPath =
    location.pathname === "/" || location.pathname.startsWith(nursingHomePath);
  const isAestheticsPath = location.pathname.startsWith(aestheticsHomePath);

  const options = useMemo(
    () =>
      isAesthetics
        ? [
            { title: "Início", section: "banner", homePath: aestheticsHomePath },
            { title: "Categorias", section: "homecare", homePath: aestheticsHomePath },
            { title: "Ranking", section: "ranking", homePath: aestheticsHomePath },
            { title: "Sobre", section: "about", homePath: aestheticsHomePath },
            { title: "Contato", section: "footer", homePath: aestheticsHomePath },
          ]
        : [
            { title: "Início", section: "banner" },
            { title: "Home Care", section: "homecare" },
            { title: "Enfermagem especializada", section: "services" },
            { title: "Feedback", section: "feedback" },
            { title: "Contato", section: "footer" },
          ],
    [aestheticsHomePath, isAesthetics]
  );

  const navigateLanding = (path) => {
    if (location.pathname !== path) {
      history.push(path);
      setMobileMenuOpen(false);
      return;
    }

    scrollToSection("banner");
    setMobileMenuOpen(false);
  };

  const goTo = (item) => {
    if (item.path) {
      if (location.pathname !== item.path) {
        history.push(item.path);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      setMobileMenuOpen(false);
      return;
    }

    if (item.section) {
      if (item.homePath && location.pathname !== item.homePath) {
        history.push(item.homePath);
        window.setTimeout(() => {
          scrollToSection(item.section);
        }, 120);
      } else {
        scrollToSection(item.section);
      }
    }

    setMobileMenuOpen(false);
  };

  return (
    <HeaderContainer pageTheme={pageTheme} $aesthetic={useAestheticHeader}>
      <HeaderInner pageTheme={pageTheme} $aesthetic={useAestheticHeader}>
        <Brand onClick={() => goTo({ section: "banner", homePath: isAesthetics ? aestheticsHomePath : null })}>
          <BrandLogo src="/images/hafundo.jpg" alt="Logo da empresa" $aesthetic={useAestheticHeader} />
          <BrandText>
            <BrandTitle pageTheme={pageTheme} $aesthetic={useAestheticHeader}>
              {pageTheme.brand.title}
            </BrandTitle>
            <BrandSub pageTheme={pageTheme} $aesthetic={useAestheticHeader}>
              {pageTheme.brand.subtitle}
            </BrandSub>
          </BrandText>
        </Brand>

        <DesktopNav $aesthetic={useAestheticHeader}>
          {options.map((item) => (
            <NavItem
              key={item.path || item.section}
              pageTheme={pageTheme}
              $aesthetic={useAestheticHeader}
              onClick={() => goTo(item)}
            >
              {item.title}
            </NavItem>
          ))}
        </DesktopNav>

        <HeaderActions>
          <HeaderSwitch $aesthetic={useAestheticHeader}>
            {landpages.map((item) => {
              const Icon = item.icon;
              const isActive = item.key === "aesthetics" ? isAestheticsPath : isNursingPath;
              return (
                <HeaderSwitchButton
                  key={item.key}
                  type="button"
                  $active={isActive}
                  $aesthetic={useAestheticHeader}
                  onClick={() => navigateLanding(item.path)}
                >
                  <HeaderSwitchIcon>
                    <Icon />
                  </HeaderSwitchIcon>
                  <HeaderSwitchLabel>{item.title}</HeaderSwitchLabel>
                </HeaderSwitchButton>
              );
            })}
          </HeaderSwitch>

          {isAesthetics ? (
            <HeaderCta
              pageTheme={pageTheme}
              $aesthetic={useAestheticHeader}
              $secondary
              onClick={() => goTo({ section: "treatments", homePath: aestheticsHomePath })}
            >
              Catálogo completo
            </HeaderCta>
          ) : null}

          <HeaderCta
            pageTheme={pageTheme}
            $aesthetic={useAestheticHeader}
            onClick={() => window.open("https://wa.me/61998333005", "_blank")}
          >
            WhatsApp
          </HeaderCta>
        </HeaderActions>

        <MobileToggle
          pageTheme={pageTheme}
          $aesthetic={useAestheticHeader}
          type="button"
          aria-label="Abrir menu"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
        >
          <span />
          <span />
          <span />
        </MobileToggle>
      </HeaderInner>

      <MobileMenuOverlay
        open={mobileMenuOpen}
        $aesthetic={useAestheticHeader}
        onClick={() => setMobileMenuOpen(false)}
      />
      <HeaderMobileMenu open={mobileMenuOpen} pageTheme={pageTheme} $aesthetic={useAestheticHeader}>
        <MobileTop pageTheme={pageTheme} $aesthetic={useAestheticHeader}>
          Menu
        </MobileTop>
        {options.map((item) => (
          <HeaderMobileItem
            key={item.path || item.section}
            pageTheme={pageTheme}
            $aesthetic={useAestheticHeader}
            onClick={() => goTo(item)}
          >
            {item.title}
          </HeaderMobileItem>
        ))}
        {landpages.map((item) => {
          const isActive = item.key === "aesthetics" ? isAestheticsPath : isNursingPath;
          return (
            <HeaderMobileItem
              key={`switch-${item.key}`}
              button={isActive}
              pageTheme={pageTheme}
              $aesthetic={useAestheticHeader}
              onClick={() => navigateLanding(item.path)}
            >
              {item.title}
            </HeaderMobileItem>
          );
        })}
        {isAesthetics ? (
          <HeaderMobileItem
            button
            pageTheme={pageTheme}
            $aesthetic={useAestheticHeader}
            onClick={() => goTo({ section: "treatments", homePath: aestheticsHomePath })}
          >
            Catálogo completo
          </HeaderMobileItem>
        ) : null}
        <HeaderMobileItem
          button
          pageTheme={pageTheme}
          $aesthetic={useAestheticHeader}
          onClick={() => {
            window.open("https://wa.me/61998333005", "_blank");
            setMobileMenuOpen(false);
          }}
        >
          Falar no WhatsApp
        </HeaderMobileItem>
      </HeaderMobileMenu>
    </HeaderContainer>
  );
}
